import * as React from 'react';
import { Binding, Result } from 'core/infrastructure';
import { deepEquals } from 'core/utils';
import { Format, formatAsElementId } from './Utils';
import { DataGrid, DataGridCtor } from './DataGrid';
import { DataGridColumn } from './DataGrid.Column';
import { FormComponent } from './Form';
import { Res } from 'core/api';
import classnames from 'classnames';
import AutoCompleteTextBox from './AutoCompleteTextBox';
import Button from './Button';
import { showCustom } from './Dialog';
import * as TagUtils from './TagUtils';
import { MultiSelectDialog } from './MultiSelectDialog';


type MultiSelectListProps<T> = React.HTMLProps<MultiSelectList<T>> & {
	linkTo: Binding.PropertyPath<T>;
	gridHeight?: number;
	items: any[];
	itemKey: (item: any) => any;
	columns: DataGridColumn<any>[];
	itemFormat?: Format;
	dialogTitle: string;
	boundValue?: any[];
	formId?: string;
	useSingleSelection?: boolean;
	onSelectedItemsChanged?: (selectedItems: any[]) => void;
	allowedTags?: string[];
	canDelete?: ((items: any[]) => boolean) | ((items: any[]) => Promise<boolean>);
	minLengthToActivateSearch?: number;
	onCreate?: string | (() => void);
	onReload?: string | (() => void);
	allowDelete?: boolean;
} & (SortableByFlag | SortableByFunction);

type SortableByFlag = {
	allowReorder?: boolean;
	allowSortOnColumnClick?: boolean;
};

type SortableByFunction = { sortBy?: (item: any) => any; };


interface MultiSelectListState {
	errorMsg?: string;
	selectedItems: any[];
}


function removeByItemKey<T>(allItems: T[], itemsToRemove: T[], itemKey: (item: T) => any): T[] {
	const containsByKey = (items: T[], item: T) => items.some(x => deepEquals(itemKey(x), itemKey(item)));
	return allItems.filter(item => !containsByKey(itemsToRemove, item));
}

export class MultiSelectList<T> extends React.Component<MultiSelectListProps<T>, MultiSelectListState> implements FormComponent {

	private dataGrid = React.createRef<DataGrid<any>>();
	private input = React.createRef<AutoCompleteTextBox<T>>();

	constructor(props: MultiSelectListProps<T>) {
		super(props);

		if (!(props.boundValue instanceof Array))
			throw new Error('MultiSelectList no tiene un boundValue de tipo Array');

		this.state = this.createStateFromProps(props);
	}

	createStateFromProps(props: MultiSelectListProps<T>): MultiSelectListState {
		return {
			errorMsg: '',
			selectedItems: TagUtils.filterByTags(props.boundValue ?? [], props.allowedTags ?? []),
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<any[]> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.getSelectedItems());
	}

	getSelectedItems() {
		// Este método hace falta par los tests
		return this.state.selectedItems;
	}

	getAllowedItems() {
		return TagUtils.filterByTags(this.props.items, this.props.allowedTags ?? []);
	}

	getPendingItems() {
		return removeByItemKey(this.getAllowedItems(), this.getSelectedItems(), this.props.itemKey);
	}

	protected showSelectItemsDialog() {
		return showCustom<any[]>(<MultiSelectDialog title={this.props.dialogTitle} grid={{
			getItems: () => this.getPendingItems(),
			columns: this.props.columns,
			itemKey: this.props.itemKey,
			allowedTags: this.props.allowedTags,
			onCreate: this.props.onCreate,
			onReload: this.props.onReload
		}} />);
	}

	buildAutoComplete() {

		const onOptionSelected = (option: any) => {
			const selectedItems = [...this.getSelectedItems()];
			selectedItems.push(option);
			this.setState({ selectedItems }, () => {
				this.dataGrid.current!.selectItems([option]);
			});
		};

		const availableItems = this.getPendingItems();

		const onShowDialog = () => this.showSelectItemsDialog().then(result => {
			const selectedItems = [...this.getSelectedItems()];
			if (result.ok)
				this.setState({ selectedItems: selectedItems.concat(result.value) }, () => {
					this.dataGrid.current!.selectItems(this.props.useSingleSelection ? [result.value[0]] : result.value);
				});

			this.input.current!.focus();
		});

		return <AutoCompleteTextBox
			ref={this.input}
			placeholder={Res.WA.WA_MULTI_SELECT_LIST_ADD_PLACE_HOLDER()}
			availableOptions={availableItems}
			formatOption={this.props.itemFormat}
			onShowSearchDialog={onShowDialog}
			onOptionSelected={onOptionSelected}
			errorMsg={this.state.errorMsg || ''}
			disabled={this.props.disabled}
			minLengthToActivateSearch={this.props.minLengthToActivateSearch}
		/>;
	}

	// Esta función dejo que sea accesible desde el exterior por si queremos eliminar
	// desde un botón de una columna
	removeItems(items: any[]) {
		const newSelection = removeByItemKey(this.getSelectedItems(), items, this.props.itemKey);
		this.setState({ selectedItems: newSelection });
	}

	render() {

		const Grid: DataGridCtor<any> = DataGrid;

		const onRemoveItems = async () => {
			const canDelete = this.props.canDelete !== undefined ? await this.props.canDelete(this.dataGrid.current!.getSelectedItems()) : true;
			if (canDelete)
				this.removeItems(this.dataGrid.current!.getSelectedItems());
		};

		const onMoveItemUp = () => {
			const selectedItems = [...this.getSelectedItems()];
			const index = selectedItems.indexOf(this.dataGrid.current!.getSelectedItems()[0]);
			if (index > 0) {
				selectedItems.moveItem(index, index - 1);
				this.dataGrid.current!.clearSort();
				this.setState({ selectedItems }, () =>
					this.dataGrid.current!.selectItems([selectedItems[index - 1]])
				);
			}
		};

		const onMoveItemDown = () => {
			const selectedItems = [...this.getSelectedItems()];
			const index = selectedItems.indexOf(this.dataGrid.current!.getSelectedItems()[0]);
			if (index >= 0 && index < selectedItems.length - 1) {
				selectedItems.moveItem(index, index + 1);
				this.dataGrid.current!.clearSort();
				this.setState({ selectedItems }, () =>
					this.dataGrid.current!.selectItems([selectedItems[index + 1]])
				);
			}
		};

		const columns = this.props.columns;

		const orderButtons = <div className='m-t'>
			<Button disabled={this.props.disabled} id={'test_up_button'} onClick={onMoveItemUp}><i className='text-info fa fa-arrow-up'></i></Button>
			<Button disabled={this.props.disabled} id={'test_down_button'} className='m-t-xs' onClick={onMoveItemDown}><i className='text-info fa fa-arrow-down'></i></Button>
		</div>;

		const onSelectedItemsChanged = (items: any[]) => {
			if (this.props.onSelectedItemsChanged)
				return this.props.onSelectedItemsChanged(items);
		};

		const onSortChanged = (data: T[]) => {
			this.setState({ selectedItems: data });
		};

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>

			<div className='col-sm-12'>
				{this.buildAutoComplete()}

				<div className='m-t'>
					<div className='multi-select-list'>
						<Grid
							height={this.props.gridHeight || 200}
							itemKey={this.props.itemKey}
							ref={this.dataGrid}
							id={'dataGrid_' + formatAsElementId(this.getPath(), this.props.formId || '')}
							columns={columns}
							initialData={isSortableByFunc(this.props) && this.props.sortBy ? this.state.selectedItems.sortBy(this.props.sortBy) : this.state.selectedItems}
							shouldPersistState={false}
							sortable={isSortableByFlag(this.props) ? !this.props.allowReorder || this.props.allowSortOnColumnClick : false}
							onSortChanged={onSortChanged}
							multiSelect={!this.props.useSingleSelection}
							hideExportButton={true}
							maxHeight={this.props.gridHeight || 175}
							onSelectedItemsChanged={onSelectedItemsChanged}
							disabled={this.props.disabled} />
					</div>
					<div className='multi-select-list-buttons'>
						{this.props.allowDelete !== false && <Button disabled={this.props.disabled} onClick={onRemoveItems}><i className='text-info fa fa-trash-o'></i></Button>}
						{isSortableByFlag(this.props) && this.props.allowReorder ? orderButtons : null}
					</div>

				</div>
			</div>
		</div>;
	}
}

function isSortableByFlag(props: SortableByFlag | SortableByFunction): props is SortableByFlag {
	return (props as any).allowReorder !== undefined || (props as any).allowSortOnColumnClick !== undefined;
}

function isSortableByFunc(props: SortableByFlag | SortableByFunction): props is SortableByFunction {
	return (props as any).sortBy !== undefined;
}

export type MultiSelectListCtor<T> = new (props: MultiSelectListProps<T>) => React.Component<MultiSelectListProps<T>, MultiSelectListState>;
